import React, { useState } from "react";
import { Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { BsTrash } from "react-icons/bs";
import { connect } from "react-redux";
import { getTerminalStudents } from "../../actions/Terminal.action";
import { createTerminal } from "../../actions/Terminal.action";
import { years } from "../../constants/MonthsAndYears";
import { termArray } from "../../constants/termArray";
import { toast } from "react-toastify";

const AddTerminalForm = ({ grades, getTerminalStudents, createTerminal }) => {
  const [grade, setGrade] = useState("");
  const [batch, setBatch] = useState("");
  const [shift, setShift] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [term, setTerm] = useState("1");
  const [terminalSubjectId, setTerminalSubjectId] = useState("");
  const [highestMark, setHighestMark] = useState("");
  const [students, setStudents] = useState(null);
  const [studentMarks, setStudentMarks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const selectHandeler = async () => {
    if (!terminalSubjectId) {
      toast.error("Please select a Terminal Subject!");
      return;
    }

    setIsLoading(true);
    try {
      const data = await getTerminalStudents({
        batchId: parseInt(batch),
        year: parseInt(year),
        gradeId: parseInt(grade),
        shiftId: parseInt(shift),
        term: parseInt(term),
        terminalSubjectId: parseInt(terminalSubjectId),
      });

      if (data && data.length > 0) {
        setStudents(data);
        // Initialize student marks with empty values
        const initialMarks = data.map((student) => ({
          id: student.id,
          studentId: student.uid,
          studentName: student.name,
          oralMark: "0.00",
          writingMark: "0.00",
          totalMark: 0.0,
          percentage: 0.0,
        }));
        setStudentMarks(initialMarks);
      } else {
        setStudents([]);
        setStudentMarks([]);
        toast.info("No students found without terminal entries");
      }
    } catch (error) {
      toast.error("Error fetching students");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkChange = (index, field, value) => {
    const newMarks = [...studentMarks];

    // Get selected terminal subject for validation
    const selectedTerminalSubject = grades
      .find((g) => g.id === parseInt(grade))
      ?.terminalSubjects?.find((ts) => ts.id === parseInt(terminalSubjectId));

    // Validate max values
    if (field === "oralMark") {
      const maxOralMark = selectedTerminalSubject?.oralMark || 100;
      const numValue = parseFloat(value) || 0;
      if (numValue > maxOralMark) {
        toast.error(`Oral mark cannot exceed ${maxOralMark}`);
        return;
      }
    } else if (field === "writingMark") {
      const maxWritingMark = selectedTerminalSubject?.writingMark || 100;
      const numValue = parseFloat(value) || 0;
      if (numValue > maxWritingMark) {
        toast.error(`Writing mark cannot exceed ${maxWritingMark}`);
        return;
      }
    }

    newMarks[index][field] = value;

    if (field === "oralMark" || field === "writingMark") {
      const oralMark =
        field === "oralMark"
          ? parseFloat(value) || 0
          : parseFloat(newMarks[index].oralMark) || 0;
      const writingMark =
        field === "writingMark"
          ? parseFloat(value) || 0
          : parseFloat(newMarks[index].writingMark) || 0;
      const totalMark = parseFloat((oralMark + writingMark).toFixed(2));

      // Get fullMark from selected terminal subject
      const fullMark = selectedTerminalSubject?.mark || 0;
      const percentage = fullMark > 0 ? (totalMark * 100) / fullMark : 0;

      newMarks[index].totalMark = totalMark;
      newMarks[index].percentage = parseFloat(percentage.toFixed(2));
    }

    setStudentMarks(newMarks);
  };

  const removeStudent = (index) => {
    const newMarks = studentMarks.filter((_, i) => i !== index);
    setStudentMarks(newMarks);
  };

  const handleSubmit = async () => {
    // Validation
    if (!terminalSubjectId) {
      toast.error("Please select a Terminal Subject!");
      return;
    }
    if (!highestMark || highestMark <= 0) {
      toast.error("Please enter a valid highest mark!");
      return;
    }
    if (studentMarks.length === 0) {
      toast.error("Please add at least one student!");
      return;
    }

    // Get selected terminal subject for validation
    const selectedTerminalSubject = grades
      .find((g) => g.id === parseInt(grade))
      ?.terminalSubjects?.find((ts) => ts.id === parseInt(terminalSubjectId));

    // Check if all marks are filled and within valid range
    const invalidMarks = studentMarks.some((mark) => {
      const oralMark = parseFloat(mark.oralMark) || 0;
      const writingMark = parseFloat(mark.writingMark) || 0;
      const maxOralMark = selectedTerminalSubject?.oralMark || 100;
      const maxWritingMark = selectedTerminalSubject?.writingMark || 100;

      return (
        !mark.oralMark ||
        !mark.writingMark ||
        oralMark < 0 ||
        writingMark < 0 ||
        oralMark > maxOralMark ||
        writingMark > maxWritingMark
      );
    });

    if (invalidMarks) {
      toast.error(
        "Please ensure all marks are filled and within the valid range!"
      );
      return;
    }

    setIsLoading(true);
    try {
      const marks = studentMarks.map((mark) => ({
        studentId: mark.id,
        oralMark: parseFloat(mark.oralMark),
        writingMark: parseFloat(mark.writingMark),
      }));

      const success = await createTerminal({
        terminalSubjectId: parseInt(terminalSubjectId),
        batchId: parseInt(batch),
        shiftId: parseInt(shift),
        gradeId: parseInt(grade),
        year: parseInt(year),
        term: parseInt(term),
        highestMark: parseInt(highestMark),
        marks: marks,
      });

      if (success) {
        // Reset form
        setStudentMarks([]);
        setStudents(null);
        setHighestMark("");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const selectedGrade = grades?.find((g) => g.id === parseInt(grade));
  const allTerminalSubjects = selectedGrade?.terminalSubjects || [];

  // Filter terminal subjects based on selected term
  const terminalSubjects = allTerminalSubjects.filter((subject) => {
    if (!subject.terms) return true; // If no terms specified, show all
    const subjectTerms = subject.terms.split(",").map((t) => t.trim());
    return subjectTerms.includes(term.toString());
  });

  return (
    <div>
      <Card bg="white" text="dark" className={`crd shadow`}>
        <Card.Body>
          <Row>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="grade" className="d-block">
                  Class
                </label>
              </div>
              <Form.Select
                onChange={(e) => {
                  setBatch("");
                  setShift("");
                  setGrade(e.target.value);
                  setTerminalSubjectId("");
                }}
              >
                <option value={""}>Select Class</option>
                {grades &&
                  grades.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </Form.Select>
            </Col>
            {grade !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="shift" className="d-block">
                    Shift
                  </label>
                </div>
                <Form.Select
                  onChange={(e) => {
                    setBatch("");
                    setShift(e.target.value);
                  }}
                >
                  <option value={""}>Select Shift</option>
                  {grades
                    .filter((grd) => grd.id === parseInt(grade))[0]
                    .shifts.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
            {grade !== "" && shift !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="batch" className="d-block">
                    Batch
                  </label>
                </div>
                <Form.Select onChange={(e) => setBatch(e.target.value)}>
                  <option value={""}>Select Batch</option>
                  {grades
                    .filter((grd) => grd.id === parseInt(grade))[0]
                    .batches.filter((bt) => bt.shiftId === parseInt(shift))
                    .map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
            {grade !== "" && shift !== "" && batch !== "" ? (
              <Col
                md={3}
                className="d-flex justify-content-end align-items-end py-3 d-none d-md-flex"
              >
                <Button
                  onClick={selectHandeler}
                  variant="primary"
                  type="button"
                  className="btn_primary"
                  disabled={isLoading}
                >
                  {isLoading ? "Loading..." : "Get Students"}
                </Button>
              </Col>
            ) : (
              <></>
            )}
          </Row>
          <Row>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="term" className="d-block">
                  Term
                </label>
              </div>
              <Form.Select
                onChange={(e) => {
                  setTerm(e.target.value);
                  setTerminalSubjectId(""); // Reset terminal subject when term changes
                }}
                value={term}
              >
                {termArray.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="terminalSubject" className="d-block">
                  Terminal Subject
                </label>
              </div>
              <Form.Select
                onChange={(e) => setTerminalSubjectId(e.target.value)}
                value={terminalSubjectId}
              >
                <option value={""}>Select Terminal Subject</option>
                {terminalSubjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name} (Oral: {subject.oralMark}, Writing:{" "}
                    {subject.writingMark}, Total:{" "}
                    {subject.oralMark + subject.writingMark})
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="year" className="d-block">
                  Year
                </label>
              </div>
              <Form.Select
                value={year}
                onChange={(e) => setYear(e.target.value)}
              >
                {years.map((item, i) => (
                  <option key={i} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="highestMark" className="d-block">
                  Highest Mark
                </label>
              </div>
              <Form.Control
                type="number"
                placeholder="Enter highest mark"
                value={highestMark}
                onChange={(e) => setHighestMark(e.target.value)}
                min="0"
              />
            </Col>
          </Row>

          {/* Mobile Submit Button */}
          {grade !== "" && shift !== "" && batch !== "" && (
            <Row className="d-md-none">
              <Col className="py-3">
                <Button
                  onClick={selectHandeler}
                  variant="primary"
                  type="button"
                  className="btn_primary w-100"
                  disabled={isLoading}
                >
                  {isLoading ? "Loading..." : "Get Students"}
                </Button>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {students && students.length > 0 && (
        <Card bg="white" text="dark" className={`crd shadow mt-3`}>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="text-center my-auto">
                Students ({studentMarks.length})
              </h3>
            </div>
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>UID</th>
                  <th>Name</th>
                  <th>
                    Oral Mark
                    {selectedGrade?.terminalSubjects?.find(
                      (ts) => ts.id === parseInt(terminalSubjectId)
                    ) && (
                      <span className="text-muted fw-normal">
                        {" "}
                        (Max:{" "}
                        {
                          selectedGrade?.terminalSubjects?.find(
                            (ts) => ts.id === parseInt(terminalSubjectId)
                          )?.oralMark
                        }
                        )
                      </span>
                    )}
                  </th>
                  <th>
                    Writing Mark
                    {selectedGrade?.terminalSubjects?.find(
                      (ts) => ts.id === parseInt(terminalSubjectId)
                    ) && (
                      <span className="text-muted fw-normal">
                        {" "}
                        (Max:{" "}
                        {
                          selectedGrade?.terminalSubjects?.find(
                            (ts) => ts.id === parseInt(terminalSubjectId)
                          )?.writingMark
                        }
                        )
                      </span>
                    )}
                  </th>
                  <th>Total Mark</th>
                  <th>Percentage</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {studentMarks.map((mark, index) => (
                  <tr key={mark.studentId}>
                    <td>{mark.studentId}</td>
                    <td>{mark.studentName}</td>
                    <td>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={mark.oralMark}
                        onChange={(e) =>
                          handleMarkChange(index, "oralMark", e.target.value)
                        }
                        min="0"
                        max={
                          selectedGrade?.terminalSubjects?.find(
                            (ts) => ts.id === parseInt(terminalSubjectId)
                          )?.oralMark || 100
                        }
                        isInvalid={
                          mark.oralMark &&
                          parseFloat(mark.oralMark) >
                            (selectedGrade?.terminalSubjects?.find(
                              (ts) => ts.id === parseInt(terminalSubjectId)
                            )?.oralMark || 100)
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={mark.writingMark}
                        onChange={(e) =>
                          handleMarkChange(index, "writingMark", e.target.value)
                        }
                        min="0"
                        max={
                          selectedGrade?.terminalSubjects?.find(
                            (ts) => ts.id === parseInt(terminalSubjectId)
                          )?.writingMark || 100
                        }
                        isInvalid={
                          mark.writingMark &&
                          parseFloat(mark.writingMark) >
                            (selectedGrade?.terminalSubjects?.find(
                              (ts) => ts.id === parseInt(terminalSubjectId)
                            )?.writingMark || 100)
                        }
                      />
                    </td>
                    <td>
                      <span className="fw-bold text-primary">
                        {parseFloat(mark.totalMark).toFixed(2)}
                      </span>
                    </td>
                    <td>
                      <span className="fw-bold text-success">
                        {parseFloat(mark.percentage).toFixed(2)}%
                      </span>
                    </td>
                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => removeStudent(index)}
                      >
                        <BsTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="d-flex justify-content-end mt-3">
              <Button
                onClick={handleSubmit}
                className="btn_primary"
                disabled={isLoading || studentMarks.length === 0}
              >
                {isLoading ? "Creating..." : "Submit Terminal Entries"}
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {students && students.length === 0 && (
        <Card bg="white" text="dark" className={`crd shadow mt-3`}>
          <Card.Body className="text-center py-5">
            <h5 className="text-muted">
              No students found without terminal entries
            </h5>
            <p className="text-muted">
              All students in this selection already have terminal entries for
              the selected criteria.
            </p>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  grades: state.grade.grade,
});

const mapDispatchToProps = {
  getTerminalStudents,
  createTerminal,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddTerminalForm);
